import React, { useEffect, useRef, useState } from 'react';
import './CustomEditor.scss';

interface CustomEditorProps {
  widthProps: string;
  heightProps: string;
  value: string;
  onChange: (value: string) => void;
}

const MAX_LENGTH = 2000;
const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '24px', '36px'];

const findAncestorTag = (node: Node | null, tagName: string, boundary: Node | null): HTMLElement | null => {
  let current: Node | null = node;
  while (current && current !== boundary) {
    if (current.nodeType === Node.ELEMENT_NODE && (current as HTMLElement).tagName === tagName) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return null;
};

const unwrapElement = (el: HTMLElement) => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
};

export const CustomEditor: React.FC<CustomEditorProps> = ({ widthProps, heightProps, value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValidHtml = useRef<string>(value);
  const savedRangeRef = useRef<Range | null>(null);
  const [activeStates, setActiveStates] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
      lastValidHtml.current = value;
    }
  }, [value]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = document.getSelection();
      if (!editorRef.current || !sel || !editorRef.current.contains(sel.anchorNode)) return;

      if (sel.rangeCount > 0 && !sel.isCollapsed) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }

      const node = sel.rangeCount > 0 ? sel.getRangeAt(0).commonAncestorContainer : null;
      setActiveStates({
        bold: !!findAncestorTag(node, 'B', editorRef.current),
        italic: !!findAncestorTag(node, 'I', editorRef.current),
        underline: !!findAncestorTag(node, 'U', editorRef.current),
      });
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handleInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;

    if (html.length > MAX_LENGTH) {
      alert('최대 2000자 이하로 작성해주세요.');
      editorRef.current.innerHTML = lastValidHtml.current;
      return;
    }

    lastValidHtml.current = html;
    onChange(html);
  };

  const withSelection = (mutate: (range: Range) => void) => {
    const sel = window.getSelection();
    const range = savedRangeRef.current;
    if (!sel || !range || range.collapsed || !editorRef.current) return;
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;

    sel.removeAllRanges();
    sel.addRange(range);
    mutate(range);
    handleInput();
  };

  const toggleTag = (tagName: string) => {
    withSelection((range) => {
      const existing = findAncestorTag(range.commonAncestorContainer, tagName, editorRef.current);
      if (existing) {
        unwrapElement(existing);
      } else {
        const contents = range.extractContents();
        const wrapper = document.createElement(tagName);
        wrapper.appendChild(contents);
        range.insertNode(wrapper);
      }
    });
  };

  const applyInlineStyle = (styleProp: 'fontSize' | 'color' | 'backgroundColor', value: string) => {
    withSelection((range) => {
      const contents = range.extractContents();
      const span = document.createElement('span');
      span.style[styleProp] = value;
      span.appendChild(contents);
      range.insertNode(span);
    });
  };

  const insertLink = () => {
    const url = window.prompt('삽입할 링크 URL을 입력하세요:', 'https://');
    if (!url) return;

    withSelection((range) => {
      const contents = range.extractContents();
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.appendChild(contents);
      range.insertNode(anchor);
    });
  };

  return (
    <div className="CustomEditor" style={{ width: widthProps, height: heightProps }}>
      <div className="CustomEditor-toolbar">
        <button
          type="button"
          className={activeStates.bold ? 'active' : ''}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleTag('B')}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className={activeStates.italic ? 'active' : ''}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleTag('I')}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className={activeStates.underline ? 'active' : ''}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleTag('U')}
        >
          <u>U</u>
        </button>

        <span className="CustomEditor-divider" />

        <select
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) applyInlineStyle('fontSize', e.target.value);
            e.target.value = '';
          }}
        >
          <option value="" disabled>
            글자 크기
          </option>
          {FONT_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <input type="color" title="글자색" onChange={(e) => applyInlineStyle('color', e.target.value)} />
        <input type="color" title="배경색" onChange={(e) => applyInlineStyle('backgroundColor', e.target.value)} />

        <span className="CustomEditor-divider" />

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertLink}>
          링크
        </button>
      </div>

      <div
        ref={editorRef}
        className="CustomEditor-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
      />
    </div>
  );
};
